import { observer } from "mobx-react-lite";
import { FormFieldLine } from "../../../../../../components/form-field-line/form-field-line";
import { Toggle } from "../../../../../../components/inputs/toggle/toggle";
import { IOption, SelectDropdown } from "../../../../../../components/inputs/select-dropdown/select-dropdown";
import { DANGEROUS_GOODS_TYPE, LABEL_TYPE, ShippingInstructionsChangeTrigger } from "../../../../return-method-types";
import { InstrucionsWarningFieldLine } from "../../instructions-warning";
import ReturnMethodStore from "../../../../stores/return-method-store";
import { NOTE_TYPE, Note } from "../../../../../../components/note/note";
import { Input } from '../../../../../../components/inputs/input/input';
import { IValidationGroup } from '../../../../../../hooks/validation-group/validation-group-hook';
import { SHIPMENT_STATUS_ENUM } from '../../../../../manage-returns/rma-page/rma-types';
import useDashboardTranslation from '../../../../../../dashboard-translation/hooks/dashboard-translation-hook';
import dashboardSettingsStore from "../../../../../../stores/dashboard-settings-store";
import { PROFILE_CAPABILITIES } from "../../../../../../models/ProfileCapabilities";


interface ILabelTypeFields {
    returnMethod?: ReturnMethodStore;
    validationGroup: IValidationGroup;
}

const LabelTypeFields: React.FC<ILabelTypeFields> = observer(({ returnMethod, validationGroup }: ILabelTypeFields) => {
    const { getTranslation } = useDashboardTranslation();
    const scope = 'dashboard-return-method-editor';

    const shippingLabelStore = returnMethod?.shippingLabelSetting;
    const labelTypeList = shippingLabelStore?.populateLabelTypeList(getTranslation)  as IOption[];
    const shipWithPrepaidLabelCapability = dashboardSettingsStore.resolveCapability(PROFILE_CAPABILITIES.RETURN_METHOD_SHIP_WITH_PREPAID_LABEL);

    const selectedLabelType = () => {
        const labelType = shippingLabelStore?.labelType;
        if (!labelType) {
            return undefined;
        }
        return labelTypeList?.find( type => type.value === labelType)
    }

    const onLabelTypeChange = (v: LABEL_TYPE) => {
        shippingLabelStore?.setLabelType(v);
    }

    const arrivedAtHubToInternalStatusList = shippingLabelStore?.populateArrivedAtHubToInternalStatusList(getTranslation) ?? [];
    const selectedArrivedAtHubToInternalStatus = (): IOption | undefined => {
        const arrivedAtHubToInternalStatus = shippingLabelStore?.arrivedAtHubToInternalStatus;
        return arrivedAtHubToInternalStatusList.find(({ value }) => value === arrivedAtHubToInternalStatus)
    }

    const dangerousGoodsTypeList = shippingLabelStore?.populateDangerousGoodsTypeList(getTranslation) ?? [];
    const selectedDangerousGoodsType = (): IOption | undefined => {
        if (shippingLabelStore?.dangerousGoodsType === null) {
            return dangerousGoodsTypeList[0];
        }
        return dangerousGoodsTypeList.find(({ value }) => value === shippingLabelStore?.dangerousGoodsType);
    }

    const getQrCodesAreNotGuaranteedNote = shippingLabelStore?.getQrCodesAreNotGuaranteedNote(getTranslation);

    return (
        <>
            {!shippingLabelStore?.hideForwardLabelField() &&
                <FormFieldLine
                    label={getTranslation("GENERATE_FORWARD_SHIPPING_LABELS", scope)}
                    note={getTranslation("GENERATE_FORWARD_SHIPPING_LABELS_NOTE", scope)}
                >
                    <Toggle
                        label=""
                        checked={shippingLabelStore?.useForwardLabels}
                        onChange={(v) => shippingLabelStore?.setUseForwardLabels(v as boolean)}
                    />
                </FormFieldLine>
            }

            { shippingLabelStore?.showRequireSugnatureToggle() &&
                <FormFieldLine
                    label={getTranslation("REQUIRE_RECIPIENT_SIGNATURE", scope)}
                    note={shippingLabelStore?.getDefaultSignatureNote()}>
                    <Toggle
                        label=""
                        checked={shippingLabelStore?.requireSignature}
                        onChange={(v) => shippingLabelStore?.setRequireSignature(v)}
                    />
                </FormFieldLine>
            }
            { shippingLabelStore?.showBoxFreeToggle() &&
                <FormFieldLine
                    label={getTranslation("BOX_FREE", scope)}
                    note={getTranslation("BOX_FREE_NOTE", scope)}>
                    <Toggle
                        label=""
                        checked={shippingLabelStore?.boxFree}
                        disabled={shippingLabelStore?.isBoxFreeDisabled()}
                        onChange={(v) => shippingLabelStore?.setBoxFree(v)}
                    />
                </FormFieldLine>
            }
            { shippingLabelStore?.showDefaultHsCode() &&
                <FormFieldLine
                    label={getTranslation("DEFAULT_HS_CODE", scope)}
                    note={getTranslation("DEFAULT_HS_CODE_NOTE", scope)}
                    required={shippingLabelStore?.isDefaultHsCodeRequired()}
                >
                <Input
                    value={shippingLabelStore?.defaultHsCode ?? undefined}
                    onChange={(v) => shippingLabelStore?.setDefaultHsCode(v)}
                    required={shippingLabelStore?.isDefaultHsCodeRequired()}
                    validationGroup={validationGroup}
                />
              </FormFieldLine>
            }
            { shippingLabelStore?.showDangerousGoodsDropdown() &&
                <FormFieldLine
                    label={getTranslation("DANGEROUS_GOODS_TYPE", scope)}
                    note={getTranslation("DANGEROUS_GOODS_TYPE_NOTE", scope)}
                    required={false}>
                    <SelectDropdown
                        value={selectedDangerousGoodsType()}
                        options={dangerousGoodsTypeList}
                        onChange={(v) => shippingLabelStore?.setDangerousGoodsType(v?.value as DANGEROUS_GOODS_TYPE)}
                    />
                </FormFieldLine>
            }
            { shippingLabelStore?.isCreateLabelOnExchangeConfigurable() &&
                <FormFieldLine
                    label={getTranslation("CREATE_LABEL_FOR_EXCHANGE_ORDER", scope)}
                    note="">
                    <Toggle
                        label=""
                        checked={shippingLabelStore?.shouldCreateLabelOnExchangeOrder}
                        onChange={(v) => shippingLabelStore?.setShouldCreateLabelOnExchangeOrder(v)}
                    />
                </FormFieldLine>
            }
            { shippingLabelStore?.showArrivedAtHubToInternalStatusDropdown() &&
                <FormFieldLine
                    label={getTranslation("TREAT_ARRIVED_AT_HUB_TRACKING_STATUS_AS", scope)}
                    required={true}
                >
                    <SelectDropdown
                        value={selectedArrivedAtHubToInternalStatus()}
                        options={arrivedAtHubToInternalStatusList}
                        onChange={(v) => shippingLabelStore?.setArrivedAtHubToInternalStatus(v?.value as SHIPMENT_STATUS_ENUM)}
                    />
                </FormFieldLine>
            }
            { (!shipWithPrepaidLabelCapability || shipWithPrepaidLabelCapability.isDefaultCustomerCompanySupported) && <FormFieldLine
                label={getTranslation("DEFAULT_CUSTOMER_COMPANY", scope)}
                note={getTranslation("DEFAULT_CUSTOMER_COMPANY_NOTE", scope)}
                required={false}
            >
                <Input
                    value={shippingLabelStore?.defaultCustomerCompany ?? undefined}
                    onChange={(v) => shippingLabelStore?.setDefaultCustomerCompany(v)}
                    required={false}
                />
            </FormFieldLine> }
            { shippingLabelStore?.showIgnoreAddressValidationToggle() &&
                <FormFieldLine
                    label={getTranslation("IGNORE_ADDRESS_VALIDATION", scope)}
                    note={getTranslation("IGNORE_ADDRESS_VALIDATION_NOTE", scope)}>
                    <Toggle
                        label=""
                        checked={shippingLabelStore?.ignoreAddressValidation}
                        onChange={(v) => shippingLabelStore?.setIgnoreAddressValidation(v)}
                    />
                </FormFieldLine>
            }
            <FormFieldLine
                label={getTranslation("LABEL_TYPE", scope)}
                note={shippingLabelStore?.getLabelTypeSupportNote()}
            >
                <SelectDropdown
                    value={selectedLabelType()}
                    options={labelTypeList}
                    onChange={(v) => onLabelTypeChange(v?.value as LABEL_TYPE)}
                    disabled={labelTypeList?.length === 1}
                />
            </FormFieldLine>

            {!!getQrCodesAreNotGuaranteedNote &&
                <FormFieldLine label="">
                    <Note type={NOTE_TYPE.INFORMATION} body={getQrCodesAreNotGuaranteedNote} />
                </FormFieldLine>
            }

            <InstrucionsWarningFieldLine returnMethod={returnMethod} trigger={ShippingInstructionsChangeTrigger.LABEL_TYPE} />
        </>
    )
})

export default LabelTypeFields;
